import {
  Patient,
  Doctor,
  Appointment,
  Contact,
  Opportunity,
  Service,
  Insurance,
  Specialty,
  ClinicalHistory,
  AuditLog,
} from '../types'
import { formatDate, getGenderName, getStatusName } from './helpers'

interface ExcelColumn {
  header: string
  key: string
  width?: number
}

interface ExcelRow {
  [key: string]: string | number | boolean
}

// Create a beautifully formatted Excel file with borders and styling
const downloadFormattedExcel = (
  data: ExcelRow[],
  columns: ExcelColumn[],
  filename: string,
  sheetName: string,
  title: string,
) => {
  if (data.length === 0) {
    alert('No hay datos para exportar')
    return
  }

  // Create HTML table with beautiful styling that Excel will render
  const htmlContent = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8">
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>${sheetName}</x:Name>
                <x:WorksheetOptions>
                  <x:DisplayGridlines/>
                </x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <style>
          body {
            font-family: Arial, sans-serif;
          }
          .title {
            font-size: 18pt;
            font-weight: bold;
            color: #0ea5e9;
            margin-bottom: 20px;
            padding: 10px;
            border-bottom: 3px solid #0ea5e9;
          }
          .subtitle {
            font-size: 10pt;
            color: #6b7280;
            margin-bottom: 15px;
            padding-left: 10px;
          }
          table {
            border-collapse: collapse;
            width: 100%;
            font-size: 11pt;
          }
          th {
            background-color: #0ea5e9;
            color: white;
            font-weight: bold;
            text-align: left;
            padding: 12px 8px;
            border: 1px solid #0284c7;
          }
          td {
            padding: 8px;
            border: 1px solid #d1d5db;
            text-align: left;
          }
          tr:nth-child(even) {
            background-color: #f9fafb;
          }
          tr:hover {
            background-color: #f0f9ff;
          }
        </style>
      </head>
      <body>
        <div class="title">${title}</div>
        <div class="subtitle">Total de registros: ${data.length} | Fecha de exportación: ${new Date().toLocaleDateString('es-DO')}</div>
        <table>
          <thead>
            <tr>
              ${columns.map((col) => `<th>${col.header}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${data
              .map(
                (row) => `
              <tr>
                ${columns.map((col) => `<td>${row[col.key] || ''}</td>`).join('')}
              </tr>
            `,
              )
              .join('')}
          </tbody>
        </table>
      </body>
    </html>
  `

  // Create blob and download
  const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const exportPatientsToExcel = (
  patients: Patient[],
  getInsuranceName: (id: string | null) => string,
) => {
  const columns: ExcelColumn[] = [
    { header: 'ID', key: 'id', width: 150 },
    { header: 'Nombre', key: 'nombre', width: 200 },
    { header: 'Edad', key: 'edad', width: 80 },
    { header: 'Sexo', key: 'sexo', width: 100 },
    { header: 'Teléfono', key: 'telefono', width: 150 },
    { header: 'Email', key: 'email', width: 250 },
    { header: 'Dirección', key: 'direccion', width: 300 },
    { header: 'Seguro', key: 'seguro', width: 200 },
    { header: 'Fecha de Registro', key: 'fecha', width: 150 },
  ]

  const data: ExcelRow[] = patients.map((patient) => ({
    id: patient.id,
    nombre: patient.name,
    edad: patient.age,
    sexo: getGenderName(patient.gender),
    telefono: patient.phone,
    email: patient.email,
    direccion: patient.address,
    seguro: getInsuranceName(patient.insuranceId),
    fecha: formatDate(patient.createdAt),
  }))

  const timestamp = new Date().toISOString().split('T')[0]
  downloadFormattedExcel(
    data,
    columns,
    `pacientes_${timestamp}.xls`,
    'Pacientes',
    'Listado de Pacientes',
  )
}

export const exportDoctorsToExcel = (
  doctors: Doctor[],
  getSpecialtyName: (id: string) => string,
) => {
  const columns: ExcelColumn[] = [
    { header: 'ID', key: 'id', width: 150 },
    { header: 'Nombre', key: 'nombre', width: 200 },
    { header: 'Especialidad', key: 'especialidad', width: 200 },
    { header: 'Teléfono', key: 'telefono', width: 150 },
    { header: 'Email', key: 'email', width: 250 },
    { header: 'Fecha de Registro', key: 'fecha', width: 150 },
  ]

  const data: ExcelRow[] = doctors.map((doctor) => ({
    id: doctor.id,
    nombre: doctor.name,
    especialidad: getSpecialtyName(doctor.specialtyId),
    telefono: doctor.phone,
    email: doctor.email,
    fecha: formatDate(doctor.createdAt),
  }))

  const timestamp = new Date().toISOString().split('T')[0]
  downloadFormattedExcel(
    data,
    columns,
    `doctores_${timestamp}.xls`,
    'Doctores',
    'Listado de Doctores',
  )
}

export const exportAppointmentsToExcel = (
  appointments: Appointment[],
  getPatientName: (id: string) => string,
  getDoctorName: (id: string) => string,
  getServiceName: (id: string) => string,
) => {
  const columns: ExcelColumn[] = [
    { header: 'ID', key: 'id', width: 150 },
    { header: 'Paciente', key: 'paciente', width: 200 },
    { header: 'Doctor', key: 'doctor', width: 200 },
    { header: 'Servicio', key: 'servicio', width: 200 },
    { header: 'Fecha', key: 'fecha', width: 120 },
    { header: 'Hora', key: 'hora', width: 100 },
    { header: 'Duración (min)', key: 'duracion', width: 120 },
    { header: 'Estado', key: 'estado', width: 120 },
    { header: 'Notas', key: 'notas', width: 300 },
    { header: 'Fecha de Creación', key: 'fechaCreacion', width: 150 },
  ]

  const data: ExcelRow[] = appointments.map((apt) => ({
    id: apt.id,
    paciente: getPatientName(apt.patientId),
    doctor: getDoctorName(apt.doctorId),
    servicio: getServiceName(apt.serviceId),
    fecha: apt.date,
    hora: apt.time,
    duracion: apt.duration,
    estado: getStatusName(apt.status),
    notas: apt.notes || '',
    fechaCreacion: formatDate(apt.createdAt),
  }))

  const timestamp = new Date().toISOString().split('T')[0]
  downloadFormattedExcel(
    data,
    columns,
    `citas_${timestamp}.xls`,
    'Citas',
    'Listado de Citas',
  )
}

export const exportServicesToExcel = (services: Service[]) => {
  const columns: ExcelColumn[] = [
    { header: 'ID', key: 'id', width: 150 },
    { header: 'Nombre', key: 'nombre', width: 200 },
    { header: 'Duración (min)', key: 'duracion', width: 120 },
    { header: 'Precio (DOP)', key: 'precio', width: 120 },
    { header: 'Descripción', key: 'descripcion', width: 300 },
  ]

  const data: ExcelRow[] = services.map((service) => ({
    id: service.id,
    nombre: service.name,
    duracion: service.duration,
    precio: service.price,
    descripcion: service.description || '',
  }))

  const timestamp = new Date().toISOString().split('T')[0]
  downloadFormattedExcel(
    data,
    columns,
    `servicios_${timestamp}.xls`,
    'Servicios',
    'Listado de Servicios',
  )
}

export const exportInsurancesToExcel = (insurances: Insurance[]) => {
  const columns: ExcelColumn[] = [
    { header: 'ID', key: 'id', width: 150 },
    { header: 'Nombre', key: 'nombre', width: 200 },
    { header: 'Compañía', key: 'compania', width: 200 },
    { header: 'Tipo de Cobertura', key: 'cobertura', width: 150 },
    { header: 'Teléfono', key: 'telefono', width: 150 },
    { header: 'Email', key: 'email', width: 250 },
  ]

  const data: ExcelRow[] = insurances.map((insurance) => ({
    id: insurance.id,
    nombre: insurance.name,
    compania: insurance.company,
    cobertura: insurance.coverageType,
    telefono: insurance.phone,
    email: insurance.email,
  }))

  const timestamp = new Date().toISOString().split('T')[0]
  downloadFormattedExcel(
    data,
    columns,
    `seguros_${timestamp}.xls`,
    'Seguros',
    'Listado de Seguros',
  )
}

export const exportSpecialtiesToExcel = (specialties: Specialty[]) => {
  const columns: ExcelColumn[] = [
    { header: 'ID', key: 'id', width: 150 },
    { header: 'Nombre', key: 'nombre', width: 200 },
    { header: 'Descripción', key: 'descripcion', width: 400 },
  ]

  const data: ExcelRow[] = specialties.map((specialty) => ({
    id: specialty.id,
    nombre: specialty.name,
    descripcion: specialty.description,
  }))

  const timestamp = new Date().toISOString().split('T')[0]
  downloadFormattedExcel(
    data,
    columns,
    `especialidades_${timestamp}.xls`,
    'Especialidades',
    'Listado de Especialidades',
  )
}

export const exportContactsToExcel = (contacts: Contact[]) => {
  const columns: ExcelColumn[] = [
    { header: 'ID', key: 'id', width: 150 },
    { header: 'Nombre', key: 'nombre', width: 200 },
    { header: 'Empresa', key: 'empresa', width: 200 },
    { header: 'Cargo', key: 'cargo', width: 150 },
    { header: 'Teléfono', key: 'telefono', width: 150 },
    { header: 'Email', key: 'email', width: 250 },
    { header: 'Notas', key: 'notas', width: 300 },
    { header: 'Fecha de Registro', key: 'fecha', width: 150 },
  ]

  const data: ExcelRow[] = contacts.map((contact) => ({
    id: contact.id,
    nombre: contact.name,
    empresa: contact.company,
    cargo: contact.position,
    telefono: contact.phone,
    email: contact.email,
    notas: contact.notes || '',
    fecha: formatDate(contact.createdAt),
  }))

  const timestamp = new Date().toISOString().split('T')[0]
  downloadFormattedExcel(
    data,
    columns,
    `contactos_${timestamp}.xls`,
    'Contactos',
    'Listado de Contactos',
  )
}

export const exportOpportunitiesToExcel = (opportunities: Opportunity[]) => {
  const columns: ExcelColumn[] = [
    { header: 'ID', key: 'id', width: 150 },
    { header: 'Nombre', key: 'nombre', width: 200 },
    { header: 'Empresa', key: 'empresa', width: 200 },
    { header: 'Industria', key: 'industria', width: 150 },
    { header: 'Tamaño', key: 'tamano', width: 150 },
    { header: 'Ubicación', key: 'ubicacion', width: 150 },
    { header: 'Etapa', key: 'etapa', width: 150 },
    { header: 'Monto (DOP)', key: 'monto', width: 120 },
    { header: 'Probabilidad (%)', key: 'probabilidad', width: 120 },
    { header: 'Notas', key: 'notas', width: 300 },
    { header: 'Fecha de Creación', key: 'fecha', width: 150 },
  ]

  const data: ExcelRow[] = opportunities.map((opp) => ({
    id: opp.id,
    nombre: opp.name,
    empresa: opp.company,
    industria: opp.industry,
    tamano: opp.size,
    ubicacion: opp.location,
    etapa: opp.stage,
    monto: opp.amount,
    probabilidad: opp.probability,
    notas: opp.notes || '',
    fecha: formatDate(opp.createdAt),
  }))

  const timestamp = new Date().toISOString().split('T')[0]
  downloadFormattedExcel(
    data,
    columns,
    `oportunidades_${timestamp}.xls`,
    'Oportunidades',
    'Listado de Oportunidades',
  )
}

export const exportClinicalHistoriesToExcel = (
  histories: ClinicalHistory[],
  getPatientName: (id: string) => string,
) => {
  const columns: ExcelColumn[] = [
    { header: 'ID', key: 'id', width: 150 },
    { header: 'Paciente', key: 'paciente', width: 200 },
    { header: 'Doctor', key: 'doctor', width: 200 },
    { header: 'Fecha', key: 'fecha', width: 120 },
    { header: 'Motivo de Consulta', key: 'motivo', width: 300 },
    { header: 'Diagnóstico', key: 'diagnostico', width: 300 },
    { header: 'Tratamiento', key: 'tratamiento', width: 300 },
    { header: 'Fecha de Creación', key: 'fechaCreacion', width: 150 },
  ]

  const data: ExcelRow[] = histories.map((history) => ({
    id: history.id,
    paciente: getPatientName(history.patientId),
    doctor: history.doctorName,
    fecha: formatDate(history.date),
    motivo: history.personalBackground.consultationReason || '',
    diagnostico: history.diagnosis || '',
    tratamiento: history.treatment || '',
    fechaCreacion: formatDate(history.createdAt),
  }))

  const timestamp = new Date().toISOString().split('T')[0]
  downloadFormattedExcel(
    data,
    columns,
    `historiales_clinicos_${timestamp}.xls`,
    'Historiales',
    'Listado de Historiales Clínicos',
  )
}

export const exportCompaniesToExcel = (companies: any[]) => {
  const columns: ExcelColumn[] = [
    { header: 'ID', key: 'id', width: 150 },
    { header: 'Nombre', key: 'nombre', width: 200 },
    { header: 'Industria', key: 'industria', width: 150 },
    { header: 'Tamaño', key: 'tamano', width: 150 },
    { header: 'Ubicación', key: 'ubicacion', width: 150 },
    { header: 'Teléfono', key: 'telefono', width: 150 },
    { header: 'Email', key: 'email', width: 250 },
    { header: 'Sitio Web', key: 'sitioWeb', width: 250 },
    { header: 'Notas', key: 'notas', width: 300 },
    { header: 'Fecha de Registro', key: 'fecha', width: 150 },
  ]

  const data: ExcelRow[] = companies.map((company) => ({
    id: company.id,
    nombre: company.name,
    industria: company.industry,
    tamano: company.size,
    ubicacion: company.location,
    telefono: company.phone,
    email: company.email,
    sitioWeb: company.website || '',
    notas: company.notes || '',
    fecha: formatDate(company.createdAt),
  }))

  const timestamp = new Date().toISOString().split('T')[0]
  downloadFormattedExcel(
    data,
    columns,
    `empresas_${timestamp}.xls`,
    'Empresas',
    'Listado de Empresas',
  )
}

export const exportLeadsToExcel = (leads: any[]) => {
  const columns: ExcelColumn[] = [
    { header: 'ID', key: 'id', width: 150 },
    { header: 'Nombre', key: 'nombre', width: 200 },
    { header: 'Teléfono', key: 'telefono', width: 150 },
    { header: 'Email', key: 'email', width: 250 },
    { header: 'Fuente', key: 'fuente', width: 150 },
    { header: 'Estado', key: 'estado', width: 150 },
    { header: 'Servicio de Interés', key: 'servicio', width: 200 },
    { header: 'Notas', key: 'notas', width: 300 },
    { header: 'Fecha de Registro', key: 'fecha', width: 150 },
  ]

  const data: ExcelRow[] = leads.map((lead) => ({
    id: lead.id,
    nombre: lead.name,
    telefono: lead.phone,
    email: lead.email,
    fuente: lead.source,
    estado: lead.status,
    servicio: lead.interestedService,
    notas: lead.notes || '',
    fecha: formatDate(lead.createdAt),
  }))

  const timestamp = new Date().toISOString().split('T')[0]
  downloadFormattedExcel(
    data,
    columns,
    `leads_${timestamp}.xls`,
    'Leads',
    'Listado de Leads',
  )
}

export const exportAuditLogsToExcel = (auditLogs: AuditLog[]) => {
  const getActionName = (action: string): string => {
    switch (action) {
      case 'create':
        return 'Creación'
      case 'update':
        return 'Actualización'
      case 'delete':
        return 'Eliminación'
      case 'complete':
        return 'Completado'
      case 'cancel':
        return 'Cancelación'
      case 'reschedule':
        return 'Reprogramación'
      default:
        return action
    }
  }

  const columns: ExcelColumn[] = [
    { header: 'Fecha y Hora', key: 'fechaHora', width: 200 },
    { header: 'Usuario', key: 'usuario', width: 200 },
    { header: 'Acción', key: 'accion', width: 150 },
    { header: 'Módulo', key: 'modulo', width: 150 },
    { header: 'Entidad', key: 'entidad', width: 200 },
    { header: 'Detalles', key: 'detalles', width: 400 },
    { header: 'Cambios', key: 'cambios', width: 300 },
  ]

  const data: ExcelRow[] = auditLogs.map((log) => ({
    fechaHora: new Date(log.timestamp).toLocaleString('es-DO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'America/Santo_Domingo',
    }),
    usuario: log.userName,
    accion: getActionName(log.action),
    modulo: log.module.charAt(0).toUpperCase() + log.module.slice(1),
    entidad: log.entityName,
    detalles: log.details,
    cambios: log.changes
      ? log.changes
          .map((c) => `${c.field}: "${c.oldValue}" → "${c.newValue}"`)
          .join('; ')
      : '',
  }))

  const timestamp = new Date().toISOString().split('T')[0]
  downloadFormattedExcel(
    data,
    columns,
    `bitacora_auditoria_${timestamp}.xls`,
    'Bitácora',
    'Bitácora de Auditoría - Savidna',
  )
}
